import { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { VIDEO_CASES, toArabicDigits } from "@/lib/constants";

/**
 * One muted video with a tap-to-play overlay and a mute toggle.
 * No autoplay with sound, mobile-friendly.
 */
const VideoPlayer = ({ src, label, testId }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden bg-black border border-white/10 aspect-[3/4] sm:aspect-[4/5] md:aspect-video"
      data-testid={testId}
    >
      <video
        ref={ref}
        src={src}
        className="absolute inset-0 w-full h-full object-cover"
        preload="metadata"
        playsInline
        muted
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      {/* Label */}
      <div className="absolute top-3 start-3 z-[3] px-3 py-1 rounded-full bg-black/70 border border-white/15 backdrop-blur text-[11px] tracking-[0.2em] uppercase font-display font-bold text-white">
        {label}
      </div>

      {/* Mute toggle */}
      <button
        onClick={toggleMute}
        className="absolute top-3 end-3 z-[3] w-10 h-10 rounded-full bg-black/70 border border-white/15 backdrop-blur flex items-center justify-center text-white hover:bg-black/90 transition"
        aria-label={muted ? "Unmute" : "Mute"}
        data-testid={`${testId}-mute`}
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      {/* Center play/pause button */}
      <button
        onClick={toggle}
        className="absolute inset-0 z-[2] flex items-center justify-center group"
        aria-label={playing ? "Pause" : "Play"}
        data-testid={`${testId}-toggle`}
      >
        <span
          className={`w-16 h-16 rounded-full bg-[#e10600] text-white flex items-center justify-center shadow-2xl shadow-red-900/50 border-[3px] border-white transition ${
            playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
          }`}
        >
          {playing ? (
            <Pause className="w-7 h-7" fill="currentColor" />
          ) : (
            <Play className="w-7 h-7 ms-1" fill="currentColor" />
          )}
        </span>
      </button>
    </div>
  );
};

const Videos = () => {
  const { t, isRTL } = useLanguage();

  if (!VIDEO_CASES.length) return null;

  return (
    <section
      id="videos"
      className="relative py-20 md:py-28 bg-[#0c0c0c]"
      data-testid="videos-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] tracking-[0.25em] uppercase font-display text-white/70">
            <span className="w-1.5 h-1.5 rounded-full bg-[#e10600]" />
            {t.videos.eyebrow}
          </div>
          <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight">
            {t.videos.title}
          </h2>
          <p className="mt-3 text-white/60 max-w-2xl">{t.videos.subtitle}</p>
        </div>

        {VIDEO_CASES.map((c) => (
          <div key={c.id} className="mb-8 last:mb-0" data-testid={`video-case-${c.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-7 h-7 rounded-full bg-[#e10600] text-white text-xs font-black flex items-center justify-center">
                {isRTL ? toArabicDigits(c.id) : c.id}
              </span>
              <span className="text-xs tracking-[0.25em] uppercase font-display text-white/75">
                {t.videos.caseLabel} {isRTL ? toArabicDigits(c.id) : c.id}
              </span>
              <span className="h-px flex-1 bg-gradient-to-r from-white/10 via-white/5 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <VideoPlayer
                src={c.before}
                label={t.gallery.before}
                testId={`video-before-${c.id}`}
              />
              <VideoPlayer
                src={c.after}
                label={t.gallery.after}
                testId={`video-after-${c.id}`}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Videos;
